import React from "react"
import { motion } from "framer-motion"
import {Calendar, User, ArrowRight} from 'lucide-react'

const BlogPage = () => {
  const articles = [
    {
      id: 1,
      title: "How to Choose the Right Mule to Move Shed Equipment",
      excerpt: "Selecting the proper shed moving mule depends on several critical factors including load capacity, terrain conditions, and frequency of use. Learn what professionals consider when investing in equipment.",
      image: "https://static.lumi.new/6f/6f1fd98ff6a6e825cc7a43a71b83549d.png",
      date: "March 15, 2024",
      author: "MuleShed Team",
      category: "Equipment Guide"
    },
    {
      id: 2,
      title: "Understanding Shed Mule Cost: Investment vs. Operating Expenses",
      excerpt: "Breaking down the total cost of ownership for professional shed moving equipment. Compare upfront shed mule cost against long-term maintenance, fuel efficiency, and productivity gains.",
      image: "https://static.lumi.new/83/8323d6f5b2163d75439767d247c570f8.png",
      date: "March 10, 2024",
      author: "Financial Team",
      category: "Business"
    },
    {
      id: 3,
      title: "Mule 628T Spotlight: Why It's the Industry's Top Choice",
      excerpt: "An in-depth look at our flagship Mule 628T model. Discover the engineering innovations that make this the preferred choice for professional shed haulers nationwide.",
      image: "https://static.lumi.new/83/8323d6f5b2163d75439767d247c570f8.png",
      date: "March 5, 2024",
      author: "Product Team",
      category: "Product Review"
    },
    {
      id: 4,
      title: "Mule Shed Delivery: What to Expect During Equipment Transport",
      excerpt: "Our comprehensive guide to the mule shed delivery process. From order placement to final installation, understand timelines, preparation requirements, and setup procedures.",
      image: "https://static.lumi.new/c3/c325335c821a43d843140cc603cc97e1.png",
      date: "February 28, 2024",
      author: "Logistics Team",
      category: "Delivery & Setup"
    },
    {
      id: 5,
      title: "Best Practices for Operating Your Shed Moving Mule Safely",
      excerpt: "Safety protocols and operational guidelines for professional mule moving shed operations. Essential training tips to protect your crew and maximize equipment lifespan.",
      image: "https://static.lumi.new/36/3661be1428baa055a82e994af7fba1ac.png",
      date: "February 20, 2024",
      author: "Safety Director",
      category: "Safety & Training"
    },
    {
      id: 6,
      title: "New vs. Used Shed Mule for Sale: Making the Right Decision",
      excerpt: "Weighing the pros and cons of purchasing new versus certified pre-owned equipment. Financial considerations, warranty coverage, and performance expectations explained.",
      image: "https://static.lumi.new/c3/c325335c821a43d843140cc603cc97e1.png",
      date: "February 15, 2024",
      author: "Sales Team",
      category: "Buying Guide"
    }
  ]

  return (
    <div className="bg-zinc-900 min-h-screen">
      {/* Header */}
      <section className="bg-zinc-950 py-20 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl font-bold text-white mb-6">
              Industry <span className="text-orange-500">Insights & Resources</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Educational articles and informational guides about shed moving equipment, operational best practices, and industry trends. Free resources for professionals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article, index) => (
              <motion.article
                key={article.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700 hover:border-orange-500 transition-all hover:transform hover:scale-105 flex flex-col"
              >
                <div className="relative h-56">
                  <img 
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                    {article.category}
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex items-center gap-4 text-gray-400 text-sm mb-4">
                    <div className="flex items-center gap-1">
                      <Calendar size={16} />
                      <span>{article.date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <User size={16} />
                      <span>{article.author}</span>
                    </div>
                  </div>
                  <h2 className="text-xl font-bold text-white mb-3 leading-tight">
                    {article.title}
                  </h2>
                  <p className="text-gray-400 mb-6 flex-grow leading-relaxed">
                    {article.excerpt}
                  </p>
                  <button className="text-orange-500 font-bold flex items-center gap-2 hover:gap-3 transition-all group">
                    Read More 
                    <ArrowRight className="group-hover:translate-x-1 transition-transform" size={18} />
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-white mb-12 text-center">
            Frequently Asked <span className="text-orange-500">Questions</span>
          </h2>
          
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-zinc-900 p-6 rounded-lg border border-zinc-800"
            >
              <h3 className="text-xl font-bold text-white mb-3">
                What is the best mule to move shed for heavy loads?
              </h3>
              <p className="text-gray-400 leading-relaxed">
                For heavy-duty operations, models like the Mule 628T and Mule Heavy 750 are specifically engineered to handle the toughest jobs. The 628T offers 28,000 lbs capacity with exceptional maneuverability, while the Heavy 750 extends to 35,000 lbs for the largest structures.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-zinc-900 p-6 rounded-lg border border-zinc-800"
            >
              <h3 className="text-xl font-bold text-white mb-3">
                How does mule shed delivery work?
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Understanding <strong>mule shed delivery</strong> processes is important when planning equipment acquisition. Equipment is typically transported on specialized trailers, with professional setup, calibration, and operator training often included. Typical delivery timelines range from 2-4 weeks depending on location.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-zinc-900 p-6 rounded-lg border border-zinc-800"
            >
              <h3 className="text-xl font-bold text-white mb-3">
                What factors affect shed mule cost?
              </h3>
              <p className="text-gray-400 leading-relaxed">
                <strong>Shed mule cost</strong> varies based on model selection, capacity requirements, optional features like remote control systems, and financing terms. New equipment ranges from commercial-grade entry models to flagship units. This information helps professionals budget and plan their equipment investments.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-zinc-900 p-6 rounded-lg border border-zinc-800"
            >
              <h3 className="text-xl font-bold text-white mb-3">
                Do you offer financing for shed moving equipment?
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Many equipment dealers partner with financing providers to offer flexible payment options for both new and certified pre-owned inventory. Most businesses can qualify for competitive rates with manageable monthly payments that preserve working capital.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default BlogPage
